import { createClient } from '@supabase/supabase-js'
import type { Database } from '@dcp/shared/types/database'
import type { TenantTables } from '@dcp/shared/types/tenant-database'
import type { ProjectionStatus } from '@/types/projections'
import { getSupabaseUrl, getSupabaseAnonKey } from './runtime-env'

/**
 * Безопасно прочитать runtime-конфиг: getSupabase* бросают при отсутствии
 * значения, но здесь нам нужна мягкая деградация (клиент = null, auth выключен),
 * поэтому ошибку перехватываем и возвращаем undefined.
 */
function readEnvSafe(getter: () => string): string | undefined {
  try {
    return getter()
  } catch {
    return undefined
  }
}

// Create typed Supabase client
const supabaseUrl = readEnvSafe(getSupabaseUrl)
const supabaseAnonKey = readEnvSafe(getSupabaseAnonKey)

/**
 * Auth storage key shared between the main and tenant Supabase clients.
 * Both clients must use the same key to share the auth session (JWT token).
 * Format matches Supabase SDK default: sb-<projectRef>-auth-token
 */
/**
 * Safely extract auth storage key from the Supabase URL.
 * Uses try-catch to avoid crashing at module load if URL is malformed.
 */
function deriveStorageKey(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    return `sb-${new URL(url).hostname.split('.')[0]}-auth-token`
  } catch {
    console.warn('Failed to parse VITE_SUPABASE_URL for storage key:', url)
    return undefined
  }
}

export const SUPABASE_STORAGE_KEY = deriveStorageKey(supabaseUrl) ?? 'sb-default-auth-token'

function createSupabaseClient() {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Missing Supabase environment variables. Auth features will be disabled.')
    return null
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: SUPABASE_STORAGE_KEY,
    },
  })
}

export const supabase = createSupabaseClient()

/**
 * Get a guaranteed non-null Supabase client
 * @throws Error if Supabase is not configured
 */
export function getSupabaseClient() {
  if (!supabase) {
    throw new Error('Supabase not configured')
  }
  return supabase
}

// Export types from shared package
export type { Database }
export type Profile = Database['public']['Tables']['profiles']['Row']
export type Organization = Database['public']['Tables']['organizations']['Row']

// Backward-compatible type aliases sourced from tenant schema
export type Integration = TenantTables['integrations']['Row']
export type Facility = TenantTables['facilities']['Row']
export type BankAccount = TenantTables['bank_accounts']['Row']
export type Transaction = TenantTables['transactions']['Row']
export type Category = TenantTables['categories']['Row']
export type Balance = TenantTables['balances']['Row']
export type Projection = TenantTables['projections']['Row']

// DCP-386: `ProjectionStatus` is declared once, in `@/types/projections`, and only
// re-exported here. It used to be a second, structurally identical declaration —
// the same split-ownership shape that let `ProjectionFilters` drift (see below).
// `ProjectionWithRelations.status` and `ProjectionFilters.status` now resolve to
// the same symbol, so adding a status to one and not the other is impossible.
export type { ProjectionStatus } from '@/types/projections'

// Extended types with relations
export interface ProjectionWithRelations {
  id: string
  category_id: string
  facility_id: string | null
  date: string
  account_name: string | null
  transaction_type: string | null
  description: string | null
  /** Single signed amount: positive = inflow, negative = outflow (DCP-250 Phase 1). */
  amount: number
  status: ProjectionStatus
  notes: string | null
  attachment_url: string | null
  attachment_name: string | null
  priority: boolean
  include_in_report: boolean
  is_recurring: boolean
  created_by: string | null
  created_at: string
  updated_at: string
  category: {
    id: string
    name: string
    /** Nullable after DCP-250 Phase 1 — categories.type column is now nullable. */
    type: 'inflow' | 'outflow' | null
  }
  facility: {
    id: string
    name: string
    code: string | null
  } | null
}

// Filter types
//
// DCP-386: `ProjectionFilters` used to be declared TWICE — here and in
// `@/types/projections` — and that duplication is how the off-by-one shipped.
// The UI imported one copy, the query layer the other, so when the filter
// contract was described as "Date objects converted in db-tenant" only one
// copy said so, and nothing forced the two to agree. Re-exporting the single
// canonical declaration makes a divergence a compile error instead of a
// wrong query. The retired `type?: 'inflow' | 'outflow'` field is gone with
// it — it survived only in this copy and no caller read it (DCP-250 Phase 1
// made `categories.type` nullable and removed the UI that set it).
export type { ProjectionFilters } from '@/types/projections'

// Result types for better error handling
interface DbError {
  message: string
  code: string
}

interface DbSuccess<T> {
  data: T
  error: null
}

interface DbFailure {
  data: null
  error: DbError
}

export type DbResult<T> = DbSuccess<T> | DbFailure

/**
 * Typed database query helpers for public schema tables only.
 * For tenant-specific tables (integrations, facilities, categories, etc.),
 * use `createTenantDb(orgId)` from `@/lib/db-tenant`.
 */
export const db = {
  profiles: {
    async getById(userId: string): Promise<DbResult<Profile>> {
      const client = getSupabaseClient()

      const { data, error } = await client
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        return { data: null, error: { message: error.message, code: error.code } }
      }

      return { data, error: null }
    },

    async update(
      userId: string,
      updateData: Partial<{
        full_name: string | null
        role: 'admin' | 'user' | 'viewer'
        updated_at: string
      }>
    ): Promise<DbResult<Profile>> {
      const client = getSupabaseClient()

      const { data, error } = await client
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        return { data: null, error: { message: error.message, code: error.code } }
      }

      return { data, error: null }
    },
  },

  organizations: {
    async getAll(): Promise<DbResult<Organization[]>> {
      const client = getSupabaseClient()

      const { data, error } = await client
        .from('organizations')
        .select('*')
        .order('name', { ascending: true })

      if (error) {
        return { data: null, error: { message: error.message, code: error.code } }
      }

      return { data: data || [], error: null }
    },

    async getById(id: string): Promise<DbResult<Organization>> {
      const client = getSupabaseClient()

      const { data, error } = await client
        .from('organizations')
        .select('*')
        .eq('id', id)
        .single()

      if (error) {
        return { data: null, error: { message: error.message, code: error.code } }
      }

      return { data, error: null }
    },
  },
}

// Re-export tenant db helpers
export { createTenantDb, type TenantPlaidItem, type TenantPlaidAccount, type TenantPlaidTransaction } from './db-tenant'
export { getTenantClient, getTenantSchemaName, clearTenantClients } from './supabase-tenant'

// Backward-compatible Plaid type aliases (previously defined inline, now from tenant)
export type { TenantPlaidItem as PlaidItem, TenantPlaidAccount as PlaidAccount, TenantPlaidTransaction as PlaidTransaction } from './db-tenant'
